// ---------- device identity ----------
// An account with nobody in it: the backend mints an opaque id (POST /users), the device keeps
// it, and sends it back as a bearer token. No sign-up, no e-mail, no password — a game opened
// to unwind must not put a form between the player and the first cell.
//
// It is minted LAZILY, on the first save that needs the cloud, so that opening the app once and
// closing it creates nothing anywhere.
//
// What this buys and what it doesn't: the progress survives a cleared cache, a reinstall of the
// web version, and moving between the app and the web build on the same device. Elle survit
// désormais aussi à une DÉSINSTALLATION et à un changement de téléphone, mais seulement pour qui
// a noté son code de reprise — cinq mots attachés au compte, affichés dans les réglages et
// retapés sur l'autre appareil (adoptUser() plus bas, `users.recovery_code` côté serveur). Sans ce
// code, le jeton part avec l'application et la progression reste dans une ligne que plus personne
// ne sait désigner.
//
// L'autre porte, une identité Google rattachée à la même ligne, reste ouverte et non empruntée :
// `users.google_sub` est là pour ça (cf. IDEES.md).

import { createUser, fetchMe } from './api';

const KEY = 'codigrid:user:v1';
const USERNAME_KEY = 'codigrid:username:v1';

/**
 * The API is async although localStorage is not, on purpose: the durable home for this token on
 * Android is @capacitor/preferences (Android SharedPreferences, which survives the WebView
 * storage being evicted), and its API is async. Switching is then these two functions and
 * nothing else — no caller changes.
 *
 *   npm i @capacitor/preferences
 *   import { Preferences } from '@capacitor/preferences';
 *   read  → (await Preferences.get({ key: KEY })).value
 *   write → Preferences.set({ key: KEY, value })
 *
 * Guarded like the progress store: a full quota or a private-mode browser must never take the
 * game down. Playing without a cloud save is a degradation; a crash is not.
 */
async function readToken(): Promise<string | null> {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

async function writeToken(token: string | null): Promise<void> {
  try {
    if (token === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, token);
  } catch {
    /* no storage: this session syncs, the next one starts over */
  }
}

/** Same guarded pattern as the token itself, next to it rather than in a module of its own —
 *  this is the only other thing that needs to know about the pseudo. */
async function readUsername(): Promise<string | null> {
  try {
    return localStorage.getItem(USERNAME_KEY);
  } catch {
    return null;
  }
}

async function writeUsername(username: string | null): Promise<void> {
  try {
    if (username === null) localStorage.removeItem(USERNAME_KEY);
    else localStorage.setItem(USERNAME_KEY, username);
  } catch {
    /* no storage: same degradation as the token */
  }
}

let cached: string | null | undefined;
let cachedUsername: string | null | undefined;
let minting: Promise<string | null> | null = null;

/** The token if this device already has one, without creating anything. */
export async function currentUser(): Promise<string | null> {
  if (cached === undefined) cached = await readToken();
  return cached;
}

/** The cached pseudo, without any network call — null both when there is no account yet and
 *  when there is one but this device never learned its pseudo (cf. refreshUsername). */
export async function currentUsername(): Promise<string | null> {
  if (cachedUsername === undefined) cachedUsername = await readUsername();
  return cachedUsername;
}

/** Ceux qui affichent le pseudo et doivent le suivre — l'écran « Moi » (src/profile.ts), dont le
 *  crayon ouvre le dialogue de Réglages : sans ce signal, le nom resterait l'ancien sous le
 *  dialogue refermé. Un écouteur plutôt qu'un rappel passé au dialogue : le pseudo change aussi
 *  par d'autres chemins (compte créé, code de reprise), et l'écran doit les suivre tous. */
const usernameListeners: Array<(username: string) => void> = [];

/** Sets the cached pseudo — called once a save is known good: right after an account is minted
 *  (its default `guestN`) or right after `PATCH /me` confirms a new one. */
export async function setUsername(username: string): Promise<void> {
  cachedUsername = username;
  await writeUsername(username);
  for (const fn of usernameListeners) fn(username);
}

export function onUsernameChange(fn: (username: string) => void): void {
  usernameListeners.push(fn);
}

/**
 * Re-reads the pseudo from the server. For a device that already holds a
 * token but never cached it alongside — an account that existed before one of these
 * features shipped, or a cache that lost its keys without losing `KEY`. A no-op if there is no
 * account at all: this must never be the thing that creates one just because Réglages was
 * opened.
 */
export async function refreshUsername(): Promise<string | null> {
  const token = await currentUser();
  if (!token) return null;
  try {
    const { username } = await fetchMe(token);
    await setUsername(username);
    return username;
  } catch {
    return null; // offline, or the row is gone — leave the cache as it was
  }
}

/** Single-flight: two saves landing together must not mint two accounts and orphan one. */
export async function ensureUser(): Promise<string | null> {
  const existing = await currentUser();
  if (existing) return existing;
  if (minting) return minting;
  minting = createUser()
    .then(async ({ id, username }) => {
      cached = id;
      await writeToken(id);
      await setUsername(username);
      return id;
    })
    .catch(() => null) // offline: the local save stands, the next attempt will mint
    .finally(() => {
      minting = null;
    });
  return minting;
}

/**
 * Cet appareil devient celui d'un compte existant — un code de reprise vient d'être échangé contre
 * son jeton (cf. redeemRecoveryCode dans api.ts, et le dialogue dans prefs.ts).
 *
 * Le pseudo arrive avec la réponse, donc il est posé ici sans aller-retour de plus.
 *
 * Ce qui n'est PAS fait ici, volontairement : supprimer le compte que cet appareil s'était minté.
 * C'est peut-être le dernier endroit au monde où vit une progression que le stockage local de cet
 * appareil n'a plus (un cache vidé qui aurait gardé le jeton), et un compte anonyme abandonné ne
 * coûte qu'une ligne. Détruire par précaution
 * n'est pas une précaution.
 */
export async function adoptUser(token: string, username: string): Promise<void> {
  cached = token;
  await writeToken(token);
  await setUsername(username);
}

/** The server no longer knows this token (row deleted, database rebuilt). Dropping it lets the
 *  next save mint a fresh account instead of retrying a 401 forever. */
export async function forgetUser(): Promise<void> {
  cached = null;
  cachedUsername = null;
  await writeToken(null);
  await writeUsername(null);
}
